import React, { useState, useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useNavigate } from "react-router-dom";
import Login from "./components/Login";
import ForgotPasswordPage from "./components/ForgotPasswordPage";
import SetNewPasswordPage from "./components/SetNewPasswordPage";
import "./App.css";
import {
  getDefaultFinancialHalf,
  getFinancialYearOptions,
  getStoredFinancialYear,
  setFinancialScope,
} from "./utils/financialScope";
import { logout } from "./utils/auth";

// Role-specific routes are code-split - a given user only ever needs one of
// these bundles, not all of them upfront.
const UserLandingPage = lazy(() => import("./components/UserLandingPage"));
const ObservationTrackerPage = lazy(() => import("./components/ObservationTrackerPage"));
const ObservationTrackerAppsPage = lazy(() => import("./components/ObservationTrackerAppsPage"));
const ControlDashboard = lazy(() => import("./components/ControlDashboard"));
const ReviewerDashboard = lazy(() => import("./components/ReviewerDashboard"));
const ReviewDetail = lazy(() => import("./components/ReviewDetail"));
const Notifications = lazy(() => import("./components/Notifications"));
const UserEvidenceSubmission = lazy(() => import("./components/UserEvidenceSubmission"));
const UserSubmittedEvidence = lazy(() => import("./components/UserSubmittedEvidence"));
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const SuperAdminDashboard = lazy(() => import("./components/SuperAdminDashboard"));

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [financialYear, setFinancialYear] = useState(getStoredFinancialYear());
  const financialYearOptions = getFinancialYearOptions();

  useEffect(() => {
    console.log("Dashboard loaded, checking for user...");
    const storedUser = localStorage.getItem("user");
    console.log("Stored user:", storedUser);
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
        console.log("User set:", storedUser);
      } catch (e) {
        console.error("Error parsing user:", e);
      }
    }
    setLoading(false);
  }, []);

  const handleLogout = () => {
    logout();
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  const handleFinancialYearChange = (event) => {
    const nextFinancialYear = event.target.value;
    const nextHalf = getDefaultFinancialHalf();
    setFinancialScope(nextFinancialYear, nextHalf);
    setFinancialYear(nextFinancialYear);
  };

  return (
    <div className="dashboard">
      <nav className="navbar">
        <h2>
          Track n Trace Control Dashboard
          <select
            className="app-fy-select"
            value={financialYear}
            onChange={handleFinancialYearChange}
            aria-label="Select financial year"
          >
            {financialYearOptions.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </h2>
        <div className="nav-center">
          <Link to="/dashboard" className="nav-link">Home</Link>
          <Link to="/submit-evidence" className="nav-link">Submit Evidence</Link>
          <Link to="/my-submissions" className="nav-link">My Submissions</Link>
          <Link to="/reviewer-dashboard" className="nav-link">Reviews</Link>
          <Link to="/notifications" className="nav-link">Notifications</Link>
        </div>
        <div className="nav-right">
          <span>Welcome, {user.name || user.email}</span>
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </nav>
      <div className="content">
        <h1>Welcome to ITGC Control System</h1>
        <p>Logged in as: <strong>{user.email}</strong></p>
        <div className="quick-links">
          <Link to="/submit-evidence" className="quick-link">
            <div className="quick-link-icon">📤</div>
            <h3>Submit Evidence</h3>
            <p>Upload required evidence documents</p>
            <span className="arrow">→</span>
          </Link>
          <Link to="/my-submissions" className="quick-link">
            <div className="quick-link-icon">📋</div>
            <h3>My Submissions</h3>
            <p>Track your submitted evidence</p>
            <span className="arrow">→</span>
          </Link>
          <Link to="/reviewer-dashboard" className="quick-link">
            <div className="quick-link-icon">✓</div>
            <h3>Review Evidence</h3>
            <p>Review submitted evidence</p>
            <span className="arrow">→</span>
          </Link>
          <Link to="/notifications" className="quick-link">
            <div className="quick-link-icon">🔔</div>
            <h3>Notifications</h3>
            <p>Check feedback on your evidence</p>
            <span className="arrow">→</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const token = localStorage.getItem("token");
  return token ? children : <Navigate to="/login" />;
}

function App() {
  const routerBasename = process.env.REACT_APP_BASENAME || "/";

  return (
    <Router basename={routerBasename}>
      <div className="app-shell">
      <div className="app-main">
      <Suspense fallback={<div>Loading...</div>}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route
          path="/set-password"
          element={
            <ProtectedRoute>
              <SetNewPasswordPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/landing"
          element={
            <ProtectedRoute>
              <UserLandingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/superadmin-dashboard"
          element={
            <ProtectedRoute>
              <SuperAdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-dashboard"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <ControlDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reviewer-dashboard"
          element={
            <ProtectedRoute>
              <ReviewerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/review/:evidenceId"
          element={
            <ProtectedRoute>
              <ReviewDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/submit-evidence"
          element={
            <ProtectedRoute>
              <UserEvidenceSubmission />
            </ProtectedRoute>
          }
        />
        <Route
          path="/submit-evidence/:controlId"
          element={
            <ProtectedRoute>
              <UserEvidenceSubmission />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-submissions"
          element={
            <ProtectedRoute>
              <UserSubmittedEvidence />
            </ProtectedRoute>
          }
        />
        <Route
          path="/observation-tracker-apps"
          element={
            <ProtectedRoute>
              <ObservationTrackerAppsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/observation-tracker"
          element={
            <ProtectedRoute>
              <ObservationTrackerPage />
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<Navigate to="/landing" />} />
      </Routes>
      </Suspense>
      </div>
      </div>
    </Router>
  );
}

export default App;
